import React, { useCallback } from "react"
import { Tooltip } from "react-tooltip"

export interface FeinFormGroupsProps {
  feins: string[]
  label: string
  required: boolean
  idPrefix: string
  labelColumns?: number
  labelClass?: string
  labelClassRequired?: string

  inputClass?: string
  onChange: (feins: string[]) => void
}

export function FeinFormGroups({
  feins,
  label,
  required,
  idPrefix,
  labelColumns = 4,
  labelClass = "control-label",
  labelClassRequired = "control-label required",
  inputClass = "col-sm-4",
  onChange,
}: FeinFormGroupsProps) {
  if (feins.length === 0) {
    feins = [""]
  }

  const addHandler = useCallback(() => {
    onChange([...feins, ""])
  }, [feins, onChange])

  const removeHandler = useCallback(() => {
    onChange(feins.filter((_, i) => i !== feins.length - 1))
  }, [feins, onChange])

  // TODO: there must be a way to implement this without two nested callbacks,
  // but it works for now.
  const changeHandler = useCallback((index: number, newValue: string) => {
    onChange(feins.map((ov, i) => i === index ? newValue : ov))
  }, [feins, onChange])

  const labelId = `${idPrefix}_label`

  return (<>
    {feins.map((fein, index, feins) => {
      const isFirst = index === 0
      const isLast = index === feins.length - 1
      return (
        <div className="fein omit-child-label" key={`fein_${index}`}>
          <div className="form-group">
            {isFirst && (
              <label className={`col-sm-${labelColumns} ${required ? labelClassRequired : labelClass}`} htmlFor={`${idPrefix}_${index}`} id={labelId}>
                {label} &nbsp;
                <i className="fa fa-question-circle" data-tooltip-id="tip-fein" data-tooltip-content="The Federal Employer Identification Number (FEIN) that classifies a company for tax reporting and other business purposes. This value should be entered as a 9-digit number without punctuation." />
                <Tooltip id="tip-fein" className="registration-tooltip" />
              </label>
            )}
            {!isFirst && <div className={`col-sm-${labelColumns}`} />}
            <div className={inputClass}>
              <Input
                id={`${idPrefix}_${index}`}
                labelId={labelId}
                value={fein ?? ""}
                index={index}
                required={required}
                onChange={changeHandler}
              />
            </div>
            {feins.length > 1 && isLast && (
              <div className="col-sm-4 hidden-print" onClick={removeHandler}>
                <p className="add-another">
                  <span className="fa fa-minus-circle fa-lg text-danger remove-this" />
                        &nbsp;&nbsp;Remove this
                </p>
              </div>
            )}
          </div>
        </div>
      )
    })}

    <div className="form-group hidden-print" onClick={addHandler}>
      <div className={`col-sm-offset-${labelColumns} col-sm-${12 - labelColumns}`}>
        <p className="add-another">
          <i className="fa fa-plus-circle fa-lg text-success add-another" />
                &nbsp;&nbsp;Add Another FEIN Number
        </p>
      </div>
      <div className="col-sm-12">
        <hr style={{ margin: "5px 0 10px" }} />
      </div>
    </div>
  </>)
}

interface InputProps {
  id: string
  labelId: string
  value: string
  required: boolean
  index: number
  onChange: (index: number, value: string) => void
}
function Input({
  id,
  labelId,
  value,
  required,
  index,
  onChange,
}: InputProps) {
  const changeHandler = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(index, e.currentTarget.value)
  }, [index, onChange])

  return (
    <input
      id={id}
      aria-labelledby={labelId}
      value={value}
      onChange={changeHandler}
      className="form-control"
      placeholder="FEIN Number (9 digits)"
      data-fv-ein-message="The value is not a valid FEIN"
      required={required}
      maxLength={9}
    />
  )
}
